import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import {
  CloudUploadOutlined,
  DeleteOutline,
  ImageOutlined,
} from "@mui/icons-material";

import type {
  Advertisement,
  AdvertisementAccountType,
  AdvertisementAction,
  AdvertisementContentType,
  AdvertisementDisplayType,
  AdvertisementFrequencyPeriod,
  AdvertisementLanguage,
  AdvertisementMediaChanges,
  AdvertisementPlacement,
  AdvertisementTranslations,
  CreateAdvertisementInput,
} from "../../types/advertisement.types";

import "../../screenstyles/Advertisements.css";

type Props = {
  initialAdvertisement?: Advertisement | null;

  submitting?: boolean;

  submitLabel: string;

  onSubmit: (
    input: CreateAdvertisementInput,

    media:
      AdvertisementMediaChanges,
  ) =>
    Promise<void> |
    void;

  onCancel: () => void;
};

const MAX_IMAGE_SIZE =
  8 * 1024 * 1024;

const acceptedImageTypes =
  new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
  ]);

const languages: {
  code:
    AdvertisementLanguage;

  label:
    string;
}[] = [
  {
    code: "ar",
    label: "العربية",
  },

  {
    code: "en",
    label: "English",
  },

  {
    code: "ku",
    label: "کوردی",
  },
];

const accountTypes:
AdvertisementAccountType[] = [
  "مستخدم",
  "وكيل",
  "منصب",
];

const accountLevels = [
  "AgentA",
  "AgentB",
  "AgentC",
  "AgentD",
  "AgentE",
  "AgentF",
  "AgentG",
  "AgentH",
];

const placements: {
  value:
    AdvertisementPlacement;

  label:
    string;
}[] = [
  {
    value:
      "home_top",

    label:
      "أعلى الصفحة الرئيسية",
  },

  {
    value:
      "home_middle",

    label:
      "وسط الصفحة الرئيسية",
  },

  {
    value:
      "products_top",

    label:
      "أعلى صفحة المنتجات",
  },

  {
    value:
      "products_middle",

    label:
      "وسط صفحة المنتجات",
  },

  {
    value:
      "product_details",

    label:
      "تفاصيل المنتج",
  },

  {
  value:
    "service_details",

  label:
    "تفاصيل الخدمة",
},
  

  {
    value:
      "points",

    label:
      "صفحة النقاط",
  },

  {
    value:
      "orders",

    label:
      "صفحة الطلبات",
  },
];

function toDateTimeLocal(
  value?:
    string |
    null,
): string {
  if (!value) {
    return "";
  }

  const date =
    new Date(
      value,
    );

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "";
  }

  const offset =
    date.getTimezoneOffset();

  const local =
    new Date(
      date.getTime() -
      offset * 60000,
    );

  return local
    .toISOString()
    .slice(
      0,
      16,
    );
}

function toIso(
  value: string,
): string {
  return new Date(
    value,
  ).toISOString();
}

function validateImageFile(
  file: File,
): string | null {
  if (
    !acceptedImageTypes.has(
      file.type,
    )
  ) {
    return "يجب أن تكون الصورة بصيغة JPEG أو PNG أو WebP.";
  }

  if (
    file.size >
    MAX_IMAGE_SIZE
  ) {
    return "حجم الصورة يجب ألا يتجاوز 8 MB.";
  }

  return null;
}

const AdvertisementForm:
React.FC<Props> = ({
  initialAdvertisement,

  submitting = false,

  submitLabel,

  onSubmit,

  onCancel,
}) => {
  const mainInputRef =
    useRef<HTMLInputElement | null>(
      null,
    );

  const mobileInputRef =
    useRef<HTMLInputElement | null>(
      null,
    );

  const [
    name,
    setName,
  ] =
    useState(
      initialAdvertisement
        ?.name ??
      "",
    );

  const [
    displayType,
    setDisplayType,
  ] =
    useState<AdvertisementDisplayType>(
      initialAdvertisement
        ?.displayType ??
      "banner",
    );

  const [
    contentType,
    setContentType,
  ] =
    useState<AdvertisementContentType>(
      initialAdvertisement
        ?.contentType ??
      "custom",
    );

  const [
    priority,
    setPriority,
  ] =
    useState(
      initialAdvertisement
        ?.priority ??
      0,
    );

  const [
    translations,
    setTranslations,
  ] =
    useState<AdvertisementTranslations>(
      initialAdvertisement
        ?.translations ?? {
        ar: {
          title: "",
          description: "",
          buttonText: "",
        },
      },
    );

  const [
    selectedLanguage,
    setSelectedLanguage,
  ] =
    useState<AdvertisementLanguage>(
      initialAdvertisement
        ?.translations.ar
        ? "ar"
        : initialAdvertisement
              ?.translations.en
          ? "en"
          : "ku",
    );

  /**
   * Images
   */
  const [
    mainImageFile,
    setMainImageFile,
  ] =
    useState<File | null>(
      null,
    );

  const [
    mobileImageFile,
    setMobileImageFile,
  ] =
    useState<File | null>(
      null,
    );

  const [
    mainPreviewUrl,
    setMainPreviewUrl,
  ] =
    useState<string | null>(
      initialAdvertisement
        ?.media.imageUrl ??
      null,
    );

  const [
    mobilePreviewUrl,
    setMobilePreviewUrl,
  ] =
    useState<string | null>(
      initialAdvertisement
        ?.media
        .mobileImageUrl ??
      null,
    );

  const [
    deleteMainImage,
    setDeleteMainImage,
  ] =
    useState(false);

  const [
    deleteMobileImage,
    setDeleteMobileImage,
  ] =
    useState(false);

  const [
    selectedAccountTypes,
    setSelectedAccountTypes,
  ] =
    useState<
      AdvertisementAccountType[]
    >(
      initialAdvertisement
        ?.targeting
        .accountTypes ??
      [],
    );

  const [
    selectedAccountLevels,
    setSelectedAccountLevels,
  ] =
    useState<string[]>(
      initialAdvertisement
        ?.targeting
        .accountLevels ??
      [],
    );

  const [
    selectedTargetLanguages,
    setSelectedTargetLanguages,
  ] =
    useState<
      AdvertisementLanguage[]
    >(
      initialAdvertisement
        ?.targeting
        .languages ??
      [],
    );

  const [
    provincesText,
    setProvincesText,
  ] =
    useState(
      initialAdvertisement
        ?.targeting
        .provinces
        .join(", ") ??
      "",
    );

  const [
    newUsersOnly,
    setNewUsersOnly,
  ] =
    useState(
      initialAdvertisement
        ?.targeting
        .newUsersOnly ??
      false,
    );

  const [
    selectedPlacements,
    setSelectedPlacements,
  ] =
    useState<
      AdvertisementPlacement[]
    >(
      initialAdvertisement
        ?.placements ??
      [],
    );

  const [
    destinationAction,
    setDestinationAction,
  ] =
    useState<AdvertisementAction>(
      initialAdvertisement
        ?.destination.action ??
      "none",
    );

  const [
    destinationValue,
    setDestinationValue,
  ] =
    useState(
      initialAdvertisement
        ?.destination
        .productId ??
      initialAdvertisement
        ?.destination
        .serviceId ??
      initialAdvertisement
        ?.destination
        .categoryId ??
      initialAdvertisement
        ?.destination
        .brandId ??
      initialAdvertisement
        ?.destination.url ??
      "",
    );

  const [
    startAt,
    setStartAt,
  ] =
    useState(
      toDateTimeLocal(
        initialAdvertisement
          ?.startAt,
      ),
    );

  const [
    endAt,
    setEndAt,
  ] =
    useState(
      toDateTimeLocal(
        initialAdvertisement
          ?.endAt,
      ),
    );

  const [
    frequencyEnabled,
    setFrequencyEnabled,
  ] =
    useState(
      initialAdvertisement
        ?.frequency.enabled ??
      false,
    );

  const [
    maxImpressions,
    setMaxImpressions,
  ] =
    useState(
      initialAdvertisement
        ?.frequency
        .maxImpressionsPerUser ??
      5,
    );

  const [
    frequencyPeriod,
    setFrequencyPeriod,
  ] =
    useState<AdvertisementFrequencyPeriod>(
      initialAdvertisement
        ?.frequency.period ??
      "day",
    );

  const [
    showOnce,
    setShowOnce,
  ] =
    useState(
      initialAdvertisement
        ?.frequency.showOnce ??
      false,
    );

  const [
    validationError,
    setValidationError,
  ] =
    useState<
      string | null
    >(null);

  /**
   * Revoke local object URLs.
   */
  useEffect(() => {
    if (
      !mainImageFile
    ) {
      return;
    }

    const objectUrl =
      URL.createObjectURL(
        mainImageFile,
      );

    setMainPreviewUrl(
      objectUrl,
    );

    return () => {
      URL.revokeObjectURL(
        objectUrl,
      );
    };
  }, [
    mainImageFile,
  ]);

  useEffect(() => {
    if (
      !mobileImageFile
    ) {
      return;
    }

    const objectUrl =
      URL.createObjectURL(
        mobileImageFile,
      );

    setMobilePreviewUrl(
      objectUrl,
    );

    return () => {
      URL.revokeObjectURL(
        objectUrl,
      );
    };
  }, [
    mobileImageFile,
  ]);

  const currentTranslation =
    translations[
      selectedLanguage
    ];

  const isLanguageEnabled =
    Boolean(
      currentTranslation,
    );

  const provinces =
    useMemo(
      () =>
        provincesText
          .split(",")
          .map(
            value =>
              value.trim(),
          )
          .filter(
            Boolean,
          ),
      [
        provincesText,
      ],
    );

  const toggleArrayValue =
    <T extends string>(
      current: T[],

      value: T,

      setter:
        React.Dispatch<
          React.SetStateAction<
            T[]
          >
        >,
    ) => {
      setter(
        current.includes(
          value,
        )
          ? current.filter(
              item =>
                item !==
                value,
            )
          : [
              ...current,
              value,
            ],
      );
    };

  const toggleLanguage =
    (
      language:
        AdvertisementLanguage,
    ) => {
      setTranslations(
        current => {
          if (
            current[
              language
            ]
          ) {
            const copy = {
              ...current,
            };

            delete copy[
              language
            ];

            return copy;
          }

          return {
            ...current,

            [language]: {
              title: "",
              description:
                "",
              buttonText:
                "",
            },
          };
        },
      );
    };

  const updateTranslation =
    (
      field:
        | "title"
        | "description"
        | "buttonText",

      value:
        string,
    ) => {
      setTranslations(
        current => ({
          ...current,

          [selectedLanguage]: {
            title:
              current[
                selectedLanguage
              ]?.title ??
              "",

            description:
              current[
                selectedLanguage
              ]?.description ??
              "",

            buttonText:
              current[
                selectedLanguage
              ]?.buttonText ??
              "",

            [field]:
              value,
          },
        }),
      );
    };

  const handleMainImageSelection =
    (
      file?: File,
    ) => {
      if (!file) {
        return;
      }

      const error =
        validateImageFile(
          file,
        );

      if (error) {
        setValidationError(
          error,
        );

        return;
      }

      setValidationError(
        null,
      );

      setDeleteMainImage(
        false,
      );

      setMainImageFile(
        file,
      );
    };

  const handleMobileImageSelection =
    (
      file?: File,
    ) => {
      if (!file) {
        return;
      }

      const error =
        validateImageFile(
          file,
        );

      if (error) {
        setValidationError(
          error,
        );

        return;
      }

      setValidationError(
        null,
      );

      setDeleteMobileImage(
        false,
      );

      setMobileImageFile(
        file,
      );
    };

  const clearMainImage =
    () => {
      setMainImageFile(
        null,
      );

      setMainPreviewUrl(
        null,
      );

      setDeleteMainImage(
        Boolean(
          initialAdvertisement
            ?.media
            .imageUrl,
        ),
      );

      if (
        mainInputRef.current
      ) {
        mainInputRef.current.value =
          "";
      }
    };

  const clearMobileImage =
    () => {
      setMobileImageFile(
        null,
      );

      setMobilePreviewUrl(
        null,
      );

      setDeleteMobileImage(
        Boolean(
          initialAdvertisement
            ?.media
            .mobileImageUrl,
        ),
      );

      if (
        mobileInputRef.current
      ) {
        mobileInputRef.current.value =
          "";
      }
    };

  const buildDestination =
    () => {
      switch (
        destinationAction
      ) {
        case "product":
          return {
            action:
              destinationAction,

            productId:
              destinationValue.trim(),
          };

        case "service":
          return {
            action:
              destinationAction,

            serviceId:
              destinationValue.trim(),
          };

        case "category":
          return {
            action:
              destinationAction,

            categoryId:
              destinationValue.trim(),
          };

        case "brand":
          return {
            action:
              destinationAction,

            brandId:
              destinationValue.trim(),
          };

        case "url":
          return {
            action:
              destinationAction,

            url:
              destinationValue.trim(),
          };

        default:
          return {
            action:
              "none" as const,
          };
      }
    };

  const handleSubmit =
    async (
      event:
        React.FormEvent,
    ) => {
      event.preventDefault();

      setValidationError(
        null,
      );

      if (
        !name.trim()
      ) {
        setValidationError(
          "اسم الإعلان مطلوب.",
        );

        return;
      }

      if (
        Object.keys(
          translations,
        ).length ===
        0
      ) {
        setValidationError(
          "يجب إضافة لغة واحدة على الأقل.",
        );

        return;
      }

      for (
        const [
          language,
          translation,
        ] of Object.entries(
          translations,
        )
      ) {
        if (
          !translation
            ?.title
            .trim()
        ) {
          setValidationError(
            `عنوان الإعلان مطلوب للغة ${language}.`,
          );

          return;
        }
      }

      /**
       * Main image must exist after this save.
       */
      if (
        !mainImageFile &&
        !initialAdvertisement
          ?.media
          .imageUrl
      ) {
        setValidationError(
          "الصورة الرئيسية للإعلان مطلوبة.",
        );

        return;
      }

      if (
        deleteMainImage &&
        !mainImageFile
      ) {
        setValidationError(
          "يجب اختيار صورة رئيسية جديدة. لا يمكن حفظ الإعلان بدون صورة رئيسية.",
        );

        return;
      }

      if (
        selectedPlacements.length ===
        0
      ) {
        setValidationError(
          "اختر مكان ظهور واحد على الأقل.",
        );

        return;
      }

      if (
        !startAt ||
        !endAt
      ) {
        setValidationError(
          "تاريخ البداية والنهاية مطلوبان.",
        );

        return;
      }

      if (
        new Date(
          startAt,
        ) >=
        new Date(
          endAt,
        )
      ) {
        setValidationError(
          "تاريخ النهاية يجب أن يكون بعد تاريخ البداية.",
        );

        return;
      }

      if (
        destinationAction !==
          "none" &&
        !destinationValue.trim()
      ) {
        setValidationError(
          "بيانات الوجهة مطلوبة.",
        );

        return;
      }

      await onSubmit(
        {
          name:
            name.trim(),

          displayType,

          contentType,

          translations,

          targeting: {
            accountTypes:
              selectedAccountTypes,

            accountLevels:
              selectedAccountLevels,

            languages:
              selectedTargetLanguages,

            provinces,

            newUsersOnly,
          },

          placements:
            selectedPlacements,

          destination:
            buildDestination(),

          priority:
            Number(
              priority,
            ),

          startAt:
            toIso(
              startAt,
            ),

          endAt:
            toIso(
              endAt,
            ),

          frequency: {
            enabled:
              frequencyEnabled,

            maxImpressionsPerUser:
              Number(
                maxImpressions,
              ),

            period:
              frequencyPeriod,

            showOnce,
          },
        },

        {
          mainImageFile,

          mobileImageFile,

          deleteMainImage,

          deleteMobileImage,
        },
      );
    };

  const mainImageCanBeDeleted =
    initialAdvertisement
      ? ![
          "active",
          "scheduled",
        ].includes(
          initialAdvertisement.status,
        )
      : true;

  return (
    <Box
      component="form"
      className="advertisement-form"
      onSubmit={
        handleSubmit
      }
      dir="rtl"
    >
      <Stack spacing={3}>
        {validationError && (
          <div className="advertisement-form-error">
            {validationError}
          </div>
        )}

        <Paper>
          <Typography
            variant="h6"
            mb={2}
          >
            البيانات الأساسية
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="اسم الإعلان"
              value={name}
              onChange={
                event =>
                  setName(
                    event.target.value,
                  )
              }
              fullWidth
            />

            <Stack
              direction={{
                xs:
                  "column",
                md:
                  "row",
              }}
              spacing={2}
            >
              <FormControl fullWidth>
                <InputLabel>
                  نوع العرض
                </InputLabel>

                <Select
                  label="نوع العرض"
                  value={
                    displayType
                  }
                  onChange={
                    event =>
                      setDisplayType(
                        event.target
                          .value as AdvertisementDisplayType,
                      )
                  }
                >
                  <MenuItem value="banner">
                    Banner
                  </MenuItem>

                  <MenuItem value="card">
                    Card
                  </MenuItem>

                  <MenuItem value="popup">
                    Popup
                  </MenuItem>

                  <MenuItem value="fullscreen">
                    Fullscreen
                  </MenuItem>

                  <MenuItem value="native">
                    Native
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>
                  نوع المحتوى
                </InputLabel>

                <Select
                  label="نوع المحتوى"
                  value={
                    contentType
                  }
                  onChange={
                    event =>
                      setContentType(
                        event.target
                          .value as AdvertisementContentType,
                      )
                  }
                >
                  <MenuItem value="product">
                    منتج
                  </MenuItem>

                  <MenuItem value="service">
                    خدمة
                  </MenuItem>

                  <MenuItem value="category">
                    تصنيف
                  </MenuItem>

                  <MenuItem value="brand">
                    براند
                  </MenuItem>

                  <MenuItem value="url">
                    رابط
                  </MenuItem>

                  <MenuItem value="custom">
                    مخصص
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="الأولوية"
                type="number"
                value={
                  priority
                }
                onChange={
                  event =>
                    setPriority(
                      Number(
                        event.target.value,
                      ),
                    )
                }
                inputProps={{
                  min: 0,
                  max:
                    100000,
                }}
                fullWidth
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper>
          <Typography variant="h6">
            النصوص واللغات
          </Typography>

          <Typography
            color="text.secondary"
            mt={0.5}
            mb={2}
          >
            يمكنك إضافة العربية
            والإنجليزية والكردية
            السورانية.
          </Typography>

          <Tabs
            value={
              selectedLanguage
            }
            onChange={(
              _,
              value,
            ) =>
              setSelectedLanguage(
                value,
              )
            }
          >
            {languages.map(
              language => (
                <Tab
                  key={
                    language.code
                  }
                  value={
                    language.code
                  }
                  label={
                    language.label
                  }
                />
              ),
            )}
          </Tabs>

          <Divider
            sx={{
              mb: 2,
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={
                  isLanguageEnabled
                }
                onChange={() =>
                  toggleLanguage(
                    selectedLanguage,
                  )
                }
              />
            }
            label="تفعيل هذه اللغة للإعلان"
          />

          {isLanguageEnabled && (
            <Stack
              spacing={2}
              mt={2}
            >
              <TextField
                label="العنوان"
                value={
                  currentTranslation
                    ?.title ??
                  ""
                }
                onChange={
                  event =>
                    updateTranslation(
                      "title",
                      event.target.value,
                    )
                }
                fullWidth
              />

              <TextField
                label="الوصف"
                multiline
                minRows={3}
                value={
                  currentTranslation
                    ?.description ??
                  ""
                }
                onChange={
                  event =>
                    updateTranslation(
                      "description",
                      event.target.value,
                    )
                }
                fullWidth
              />

              <TextField
                label="نص الزر"
                value={
                  currentTranslation
                    ?.buttonText ??
                  ""
                }
                onChange={
                  event =>
                    updateTranslation(
                      "buttonText",
                      event.target.value,
                    )
                }
                fullWidth
              />
            </Stack>
          )}
        </Paper>

        {/* Images */}
        <Paper>
          <Typography
            variant="h6"
          >
            صور الإعلان
          </Typography>

          <Typography
            color="text.secondary"
            mt={0.5}
            mb={2}
          >
            الصيغ المدعومة:
            JPEG وPNG وWebP.
            الحد الأقصى للصورة
            الواحدة 8 MB.
          </Typography>

          <div className="advertisement-media-grid">
            <div className="advertisement-media-field">
              <div className="advertisement-media-heading">
                <div>
                  <strong>
                    الصورة الرئيسية
                  </strong>

                  <span>
                    مطلوبة لعرض الإعلان
                  </span>
                </div>

                <span className="advertisement-media-required">
                  مطلوبة
                </span>
              </div>

              <input
                ref={
                  mainInputRef
                }
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={
                  event =>
                    handleMainImageSelection(
                      event.target
                        .files?.[0],
                    )
                }
              />

              {mainPreviewUrl ? (
                <div className="advertisement-media-preview">
                  <img
                    src={
                      mainPreviewUrl
                    }
                    alt="معاينة الصورة الرئيسية"
                  />

                  <div className="advertisement-media-preview-overlay">
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={
                        <CloudUploadOutlined />
                      }
                      onClick={() =>
                        mainInputRef
                          .current
                          ?.click()
                      }
                    >
                      استبدال الصورة
                    </Button>

                    {mainImageCanBeDeleted && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        startIcon={
                          <DeleteOutline />
                        }
                        onClick={
                          clearMainImage
                        }
                      >
                        حذف
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="advertisement-upload-zone"
                  onClick={() =>
                    mainInputRef
                      .current
                      ?.click()
                  }
                >
                  <ImageOutlined />

                  <strong>
                    اختر الصورة الرئيسية
                  </strong>

                  <span>
                    اضغط لاختيار الصورة من جهازك
                  </span>

                  <small>
                    JPEG / PNG / WebP · Max 8 MB
                  </small>
                </button>
              )}

              {mainImageFile && (
                <div className="advertisement-selected-file">
                  الصورة المختارة:{" "}
                  <strong>
                    {
                      mainImageFile.name
                    }
                  </strong>
                </div>
              )}
            </div>

            <div className="advertisement-media-field">
              <div className="advertisement-media-heading">
                <div>
                  <strong>
                    صورة الهاتف
                  </strong>

                  <span>
                    اختيارية
                  </span>
                </div>

                <span className="advertisement-media-optional">
                  اختياري
                </span>
              </div>

              <input
                ref={
                  mobileInputRef
                }
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={
                  event =>
                    handleMobileImageSelection(
                      event.target
                        .files?.[0],
                    )
                }
              />

              {mobilePreviewUrl ? (
                <div className="advertisement-media-preview advertisement-media-preview-mobile">
                  <img
                    src={
                      mobilePreviewUrl
                    }
                    alt="معاينة صورة الهاتف"
                  />

                  <div className="advertisement-media-preview-overlay">
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={
                        <CloudUploadOutlined />
                      }
                      onClick={() =>
                        mobileInputRef
                          .current
                          ?.click()
                      }
                    >
                      استبدال الصورة
                    </Button>

                    <Button
                      type="button"
                      variant="outlined"
                      color="error"
                      startIcon={
                        <DeleteOutline />
                      }
                      onClick={
                        clearMobileImage
                      }
                    >
                      حذف
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="advertisement-upload-zone"
                  onClick={() =>
                    mobileInputRef
                      .current
                      ?.click()
                  }
                >
                  <ImageOutlined />

                  <strong>
                    اختر صورة الهاتف
                  </strong>

                  <span>
                    إن لم تضفها سيستخدم التطبيق الصورة الرئيسية
                  </span>

                  <small>
                    JPEG / PNG / WebP · Max 8 MB
                  </small>
                </button>
              )}

              {mobileImageFile && (
                <div className="advertisement-selected-file">
                  الصورة المختارة:{" "}
                  <strong>
                    {
                      mobileImageFile.name
                    }
                  </strong>
                </div>
              )}
            </div>
          </div>
        </Paper>

        <Paper>
          <Typography variant="h6">
            استهداف المستخدمين
          </Typography>

          <Typography
            color="text.secondary"
            mb={2}
          >
            ترك أي مجموعة فارغة
            يعني جميع المستخدمين
            ضمن تلك المجموعة.
          </Typography>

          <Typography
            fontWeight={800}
            mb={1}
          >
            أنواع الحسابات
          </Typography>

          <FormGroup row>
            {accountTypes.map(
              type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedAccountTypes.includes(
                        type,
                      )}
                      onChange={() =>
                        toggleArrayValue(
                          selectedAccountTypes,
                          type,
                          setSelectedAccountTypes,
                        )
                      }
                    />
                  }
                  label={type}
                />
              ),
            )}
          </FormGroup>

          <Divider
            sx={{
              my: 2,
            }}
          />

          <Typography
            fontWeight={800}
            mb={1}
          >
            مستويات الحساب
          </Typography>

          <FormGroup row>
            {accountLevels.map(
              level => (
                <FormControlLabel
                  key={level}
                  control={
                    <Checkbox
                      checked={selectedAccountLevels.includes(
                        level,
                      )}
                      onChange={() =>
                        toggleArrayValue(
                          selectedAccountLevels,
                          level,
                          setSelectedAccountLevels,
                        )
                      }
                    />
                  }
                  label={level}
                />
              ),
            )}
          </FormGroup>

          <Divider
            sx={{
              my: 2,
            }}
          />

          <Typography
            fontWeight={800}
            mb={1}
          >
            اللغات المستهدفة
          </Typography>

          <FormGroup row>
            {languages.map(
              language => (
                <FormControlLabel
                  key={
                    language.code
                  }
                  control={
                    <Checkbox
                      checked={selectedTargetLanguages.includes(
                        language.code,
                      )}
                      onChange={() =>
                        toggleArrayValue(
                          selectedTargetLanguages,
                          language.code,
                          setSelectedTargetLanguages,
                        )
                      }
                    />
                  }
                  label={
                    language.label
                  }
                />
              ),
            )}
          </FormGroup>

          <TextField
            label="المحافظات"
            helperText="افصل المحافظات بفاصلة، مثال: بغداد, بابل, النجف"
            value={
              provincesText
            }
            onChange={
              event =>
                setProvincesText(
                  event.target.value,
                )
            }
            fullWidth
            sx={{
              mt: 2,
            }}
          />

          <FormControlLabel
            sx={{
              mt: 2,
            }}
            control={
              <Switch
                checked={
                  newUsersOnly
                }
                onChange={
                  event =>
                    setNewUsersOnly(
                      event.target.checked,
                    )
                }
              />
            }
            label="عرض الإعلان للمستخدمين الجدد فقط"
          />
        </Paper>

        <Paper>
          <Typography
            variant="h6"
            mb={2}
          >
            أماكن ظهور الإعلان
          </Typography>

          <FormGroup>
            {placements.map(
              placement => (
                <FormControlLabel
                  key={
                    placement.value
                  }
                  control={
                    <Checkbox
                      checked={selectedPlacements.includes(
                        placement.value,
                      )}
                      onChange={() =>
                        toggleArrayValue(
                          selectedPlacements,
                          placement.value,
                          setSelectedPlacements,
                        )
                      }
                    />
                  }
                  label={
                    placement.label
                  }
                />
              ),
            )}
          </FormGroup>
        </Paper>

        <Paper>
          <Typography
            variant="h6"
            mb={2}
          >
            الوجهة عند الضغط
          </Typography>

          <FormControl fullWidth>
            <InputLabel>
              نوع الإجراء
            </InputLabel>

            <Select
              label="نوع الإجراء"
              value={
                destinationAction
              }
              onChange={
                event => {
                  setDestinationAction(
                    event.target
                      .value as AdvertisementAction,
                  );

                  setDestinationValue(
                    "",
                  );
                }
              }
            >
              <MenuItem value="none">
                بدون إجراء
              </MenuItem>

              <MenuItem value="product">
                منتج
              </MenuItem>

              <MenuItem value="service">
                خدمة
              </MenuItem>

              <MenuItem value="category">
                تصنيف
              </MenuItem>

              <MenuItem value="brand">
                براند
              </MenuItem>

              <MenuItem value="url">
                رابط خارجي
              </MenuItem>
            </Select>
          </FormControl>

          {destinationAction !==
            "none" && (
            <TextField
              sx={{
                mt: 2,
              }}
              label={
                destinationAction ===
                "url"
                  ? "الرابط"
                  : "معرّف العنصر ID"
              }
              value={
                destinationValue
              }
              onChange={
                event =>
                  setDestinationValue(
                    event.target.value,
                  )
              }
              fullWidth
            />
          )}
        </Paper>

        <Paper>
          <Typography
            variant="h6"
            mb={2}
          >
            مدة الإعلان
          </Typography>

          <Stack
            direction={{
              xs:
                "column",
              md:
                "row",
            }}
            spacing={2}
          >
            <TextField
              label="تاريخ ووقت البداية"
              type="datetime-local"
              value={startAt}
              onChange={
                event =>
                  setStartAt(
                    event.target.value,
                  )
              }
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            <TextField
              label="تاريخ ووقت النهاية"
              type="datetime-local"
              value={endAt}
              onChange={
                event =>
                  setEndAt(
                    event.target.value,
                  )
              }
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Stack>
        </Paper>

        <Paper>
          <Typography variant="h6">
            تكرار الظهور
          </Typography>

          <Typography
            color="text.secondary"
            mb={2}
          >
            حدد عدد مرات ظهور
            الإعلان للمستخدم
            والفترة الزمنية
            المعتمدة لهذا الحد.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={
                  frequencyEnabled
                }
                onChange={
                  event =>
                    setFrequencyEnabled(
                      event.target.checked,
                    )
                }
              />
            }
            label="تفعيل تحديد مرات الظهور"
          />

          {frequencyEnabled && (
            <Stack
              direction={{
                xs:
                  "column",
                md:
                  "row",
              }}
              spacing={2}
              mt={2}
            >
              <TextField
                label="الحد الأقصى لكل مستخدم"
                type="number"
                value={
                  maxImpressions
                }
                onChange={
                  event =>
                    setMaxImpressions(
                      Number(
                        event.target.value,
                      ),
                    )
                }
                inputProps={{
                  min: 1,
                  max: 1000,
                }}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>
                  الفترة
                </InputLabel>

                <Select
                  label="الفترة"
                  value={
                    frequencyPeriod
                  }
                  onChange={
                    event =>
                      setFrequencyPeriod(
                        event.target
                          .value as AdvertisementFrequencyPeriod,
                      )
                  }
                >
                  <MenuItem value="hour">
                    كل ساعة
                  </MenuItem>

                  <MenuItem value="day">
                    يوميًا
                  </MenuItem>

                  <MenuItem value="week">
                    أسبوعيًا
                  </MenuItem>

                  <MenuItem value="month">
                    شهريًا
                  </MenuItem>

                  <MenuItem value="total">
                    طوال مدة الإعلان
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          <FormControlLabel
            sx={{
              mt: 2,
            }}
            control={
              <Switch
                checked={showOnce}
                onChange={
                  event =>
                    setShowOnce(
                      event.target.checked,
                    )
                }
              />
            }
            label="عرض الإعلان مرة واحدة فقط للمستخدم"
          />
        </Paper>

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            disabled={
              submitting
            }
          >
            إلغاء
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={
              submitting
            }
          >
            {submitting
              ? "جاري الحفظ والرفع..."
              : submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AdvertisementForm;